import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useEntityList } from '../../api/hooks/useEntity'
import { ENTITY, type SchoolClass, type Student, type Subject, type Document } from '../../types/entities'
import { useAuth } from '../../auth/AuthContext'

interface Step {
  key: string
  title: string
  hint: string
  cta: string
  link: string
  done: boolean
}

/**
 * Welcome-чеклист для нового учителя. Появляется на /app до тех пор,
 * пока не выполнены все 4 шага, ИЛИ пока пользователь не скрыл его явно
 * через «Я разобрался(лась)».
 *
 * Состояние «скрыт» хранится в localStorage per-teacher.
 * Состояние «выполнен» вычисляется автоматически по факту существования
 * данных (классов, учеников, предметов, документов).
 */
export function OnboardingChecklist() {
  const { t } = useTranslation()
  const { teacher } = useAuth()
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const students = useEntityList<Student>(ENTITY.student)
  const subjects = useEntityList<Subject>(ENTITY.subject)
  const documents = useEntityList<Document>(ENTITY.document)

  const dismissKey = teacher ? `edpad_onboarding_dismissed_${teacher.id}` : ''
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!dismissKey) return false
    return localStorage.getItem(dismissKey) === '1'
  })

  // Если teacher сменился (выход/вход) — обновим dismissed
  useEffect(() => {
    if (!dismissKey) return
    setDismissed(localStorage.getItem(dismissKey) === '1')
  }, [dismissKey])

  const steps: Step[] = useMemo(() => {
    const hasClass = (classes.data ?? []).some((c) => !c.archivedAt && !c.deletedAt)
    const hasStudent = (students.data ?? []).some((s) => !s.deletedAt)
    const hasSubject = (subjects.data ?? []).some((s) => !s.deletedAt)
    const hasDoc = (documents.data ?? []).some((d) => !d.deletedAt)
    return [
      {
        key: 'class', done: hasClass,
        title: t('onboardingChecklist.classTitle'),
        hint: t('onboardingChecklist.classHint'),
        cta: t('onboardingChecklist.classCta'), link: '/app/classes',
      },
      {
        key: 'student', done: hasStudent,
        title: t('onboardingChecklist.studentTitle'),
        hint: t('onboardingChecklist.studentHint'),
        cta: t('onboardingChecklist.studentCta'), link: '/app/students',
      },
      {
        key: 'subject', done: hasSubject,
        title: t('onboardingChecklist.subjectTitle'),
        hint: t('onboardingChecklist.subjectHint'),
        cta: t('onboardingChecklist.subjectCta'), link: '/app/subjects',
      },
      {
        key: 'doc', done: hasDoc,
        title: t('onboardingChecklist.docTitle'),
        hint: t('onboardingChecklist.docHint'),
        cta: t('onboardingChecklist.docCta'), link: '/app/tools/lesson-plan',
      },
    ]
  }, [classes.data, students.data, subjects.data, documents.data, t])

  const doneCount = steps.filter((s) => s.done).length
  const isLoading =
    classes.isLoading || students.isLoading || subjects.isLoading || documents.isLoading

  // Не показываем если: явно скрыт, все шаги выполнены, или ещё грузится
  if (!teacher || dismissed || doneCount === steps.length || isLoading) return null

  const handleDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, '1')
    setDismissed(true)
  }

  return (
    <div className="bg-gradient-to-br from-primary-soft/40 to-white border border-purple-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
            <span>👋</span> {t('onboardingChecklist.greeting', { name: teacher.displayName.split(' ').slice(-1)[0] || t('onboardingChecklist.teacherFallback') })}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('onboardingChecklist.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs bg-white border border-purple-200 text-primary font-semibold px-2 py-1 rounded">
            {doneCount} / {steps.length}
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
            title={t('onboardingChecklist.dismissTitle')}
          >
            {t('onboardingChecklist.dismiss')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {steps.map((s) => (
          <StepCard key={s.key} step={s} />
        ))}
      </div>
    </div>
  )
}

function StepCard({ step }: { step: Step }) {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
        step.done
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:border-primary/40'
      }`}
    >
      <div
        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
          step.done ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400 border border-gray-300'
        }`}
      >
        {step.done ? '✓' : ''}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${step.done ? 'text-green-900 line-through' : 'text-gray-900'}`}>
          {step.title}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{step.hint}</div>
        {!step.done && (
          <Link to={step.link} className="text-xs text-primary hover:underline mt-1 inline-block">
            {step.cta} →
          </Link>
        )}
      </div>
    </div>
  )
}
